import React from "react";
import { Box, Skeleton, Paper, useMediaQuery, useTheme } from "@mui/material";
import PageContainer from "../PageContainer";

const DashboardSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobileDashboard = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobileDashboard) {
    return (
      <PageContainer sx={{ mt: -1, pt: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rounded" height={68} sx={{ borderRadius: 2.5 }} />

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2.25,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 118px", gap: 1.5 }}>
              <Box>
                <Skeleton variant="text" width="70%" height={54} />
                <Skeleton variant="text" width="90%" height={24} />
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Skeleton variant="rounded" width={140} height={42} />
                  <Skeleton variant="rounded" width={140} height={42} />
                </Box>
              </Box>
              <Skeleton variant="rounded" width={118} height={118} sx={{ justifySelf: "center" }} />
            </Box>
          </Paper>

          <Skeleton variant="text" width={170} height={42} />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
            {[...Array(4)].map((_, index) => (
              <Paper
                key={`mobile-quick-action-skeleton-${index}`}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Skeleton variant="circular" width={52} height={52} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" height={26} />
                <Skeleton variant="text" width="95%" height={20} />
                <Skeleton variant="text" width="75%" height={20} />
              </Paper>
            ))}
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
            }}
          >
            <Skeleton variant="text" width="50%" height={28} sx={{ mb: 1 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              {[...Array(4)].map((_, index) => (
                <Skeleton key={`mobile-stats-skeleton-${index}`} variant="rounded" height={74} />
              ))}
            </Box>
          </Paper>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ mt: -1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr",
            lg: "1fr",
            "@media (min-width: 1200px)": {
              gridTemplateColumns: "2.8fr 1.1fr",
            },
          },
          gap: 4,
          mt: 0,
          mb: 0,
        }}
      >
        {/* Main content area: hero + charts (left column) */}
        <Box>
          {/* Hero/Featured Carousel Skeleton */}
          <Skeleton
            variant="rounded"
            height={240}
            sx={{
              borderRadius: 2,
              mb: 4,
            }}
          />

          {/* Charts section */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                "@media (min-width: 1200px)": {
                  gridTemplateColumns: "1fr 1fr",
                },
              },
              gap: 4,
            }}
          >
            {/* Line Chart Skeleton */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={250} />
            </Paper>

            {/* Pie Chart Skeleton */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
              <Skeleton
                variant="circular"
                width={200}
                height={200}
                sx={{ mx: "auto" }}
              />
            </Paper>
          </Box>
        </Box>

        {/* Sidebar (right column) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {/* Stats Panel Skeleton */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1, md: 1.5 },
            }}
          >
            {[...Array(4)].map((_, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 1.5 },
                  minHeight: { xs: 70, md: 80 },
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={40}
                  height={40}
                  sx={{ borderRadius: 2 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Calendar Skeleton */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Skeleton
              variant="text"
              width="50%"
              height={32}
              sx={{ mb: 2, mx: "auto" }}
            />

            {/* Calendar grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1,
                mb: 2,
              }}
            >
              {/* Day headers */}
              {[...Array(7)].map((_, i) => (
                <Skeleton key={i} variant="text" height={20} />
              ))}
            </Box>

            {/* Calendar days */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1,
              }}
            >
              {[...Array(35)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    aspectRatio: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    </PageContainer>
  );
};

export default DashboardSkeleton;
